import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Search, Filter, Clock, Users, Monitor, ChevronRight, X, Pencil, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const CATEGORY_COLOR = {
  Program: "#f68b1f",
  Tool: "#0091be",
  Certification: "#7ac143",
};

const ALL_CATEGORIES = ["Program", "Tool", "Certification"];

export default function Programs() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);   // program object when detail modal open
  const [editing, setEditing] = useState(null); // program object or "new"
  const [searchParams, setSearchParams] = useSearchParams();

  // When the global command palette navigates here with ?id=<program>,
  // auto-open that program's detail modal so users land right on the answer
  // instead of the full grid. The param is stripped afterward so refreshes
  // don't keep reopening the same modal.
  useEffect(() => {
    const wanted = searchParams.get("id");
    if (!wanted || items.length === 0) return;
    const found = items.find((p) => p.id === wanted);
    if (found) {
      setOpen(found);
      // Clear the query param so back/refresh works naturally.
      const next = new URLSearchParams(searchParams);
      next.delete("id");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const reload = () => {
    setLoading(true);
    return api.get("/programs")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((p) => {
      if (p.category) s.add(p.category);
      (p.also_in || []).forEach((c) => c && s.add(c));
    });
    return ["All", ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const scored = items
      .filter((p) => {
        if (cat !== "All") {
          const inCat = p.category === cat || (Array.isArray(p.also_in) && p.also_in.includes(cat));
          if (!inCat) return false;
        }
        if (!needle) return true;
        const name = (p.name || "").toLowerCase();
        if (name.includes(needle)) return true;
        const blob = [
          p.audience, p.format, p.duration, p.description, p.notes,
          ...(p.key_outcomes || []),
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(needle);
      });
    if (!needle) return scored;
    // Rank: name-start > name-contains > body-only. Preserve relative order otherwise.
    const rank = (p) => {
      const name = (p.name || "").toLowerCase();
      if (name.startsWith(needle)) return 0;
      if (name.includes(needle)) return 1;
      return 2;
    };
    return [...scored].sort((a, b) => rank(a) - rank(b));
  }, [items, q, cat]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link
        to="/"
        data-testid="programs-back"
        className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink"
      >
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="rounded-2xl bg-white border-2 border-[#0091be] mt-3 overflow-hidden"
        data-testid="programs-header"
      >
        <div className="h-1.5 w-full bg-[#0091be]" />
        <div className="p-5 sm:p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#0091be] text-white">
            <FileText size={22} strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <div className="tc-eyebrow text-[#0091be]">Programs</div>
            <h1 className="font-display text-2xl sm:text-3xl text-tc-ink leading-tight">Program Sums</h1>
            <p className="text-sm text-slate-500 mt-1">
              Searchable summaries for every True Colors program, tool, and certification.
            </p>
          </div>
          {canEdit && (
            <button
              onClick={() => setEditing("new")}
              data-testid="programs-add"
              className="h-10 sm:h-11 px-3.5 rounded-xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all shrink-0"
              style={{ background: "#0091be" }}
            >
              <Plus size={16} /> Add
            </button>
          )}
        </div>
      </motion.div>

      {/* Search + filters */}
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, audience, outcome..."
            data-testid="programs-search"
            className="w-full h-11 pl-10 pr-4 rounded-xl border-2 border-slate-200 focus:border-[#0091be] focus:outline-none text-sm"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="programs-category-filters">
          {categories.map((c) => {
            const active = cat === c;
            const color = c === "All" ? "#083050" : CATEGORY_COLOR[c] || "#083050";
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`programs-filter-${c.toLowerCase()}`}
                className={`h-11 px-4 rounded-xl text-sm font-bold whitespace-nowrap transition-all border-2 ${active ? "text-white" : "bg-white text-slate-600 hover:text-tc-ink"}`}
                style={{
                  backgroundColor: active ? color : undefined,
                  borderColor: active ? color : "#e2e8f0",
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Showing <b className="text-tc-ink">{filtered.length}</b> of {items.length}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="p-10 text-center text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 text-center text-slate-500">
          <Filter size={32} className="mx-auto opacity-40" />
          <div className="mt-3 font-bold">No programs match those filters.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="programs-grid">
          {filtered.map((p, idx) => (
            <ProgramTile key={p.id} p={p} delay={idx * 0.02} activeCat={cat} onOpen={() => setOpen(p)} />
          ))}
        </div>
      )}

      {open && (
        <ProgramDetail
          p={open}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
          onEdit={() => { setOpen(null); setEditing(open); }}
        />
      )}
      {editing && (
        <ProgramEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

function ProgramTile({ p, delay, activeCat, onOpen }) {
  // When a category filter is active and the program belongs to it (either as
  // primary category or via also_in), color the card with the filter color so
  // cross-listed items visually match the active section.
  const displayCat = (() => {
    if (activeCat && activeCat !== "All") {
      if (p.category === activeCat) return p.category;
      if (Array.isArray(p.also_in) && p.also_in.includes(activeCat)) return activeCat;
    }
    return p.category;
  })();
  const color = CATEGORY_COLOR[displayCat] || "#083050";
  const teaser = (p.description || "").split(". ")[0]?.slice(0, 140) || "";
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      onClick={onOpen}
      data-testid={`program-tile-${p.id}`}
      className="text-left bg-white border-2 rounded-2xl overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all flex flex-col"
      style={{ borderColor: color }}
    >
      <div className="h-2.5 w-full shrink-0" style={{ backgroundColor: color }} />
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <span
            className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${color}1a`, color }}
          >
            {displayCat}
          </span>
          <ChevronRight size={14} className="text-slate-300" />
        </div>
        <h3 className="font-display text-base mt-2 leading-tight text-tc-ink">{p.name}</h3>
        {teaser && <p className="text-xs text-slate-500 mt-1.5 leading-snug line-clamp-3">{teaser}{teaser.length === 140 ? "…" : "."}</p>}
        <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-slate-500">
          {p.format && <span className="inline-flex items-center gap-1"><Monitor size={11} /> {p.format}</span>}
          {p.duration && <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.duration}</span>}
          {p.audience && <span className="inline-flex items-center gap-1"><Users size={11} /> {p.audience}</span>}
        </div>
      </div>
    </motion.button>
  );
}

function ProgramDetail({ p, canEdit, onClose, onEdit }) {
  const color = CATEGORY_COLOR[p.category] || "#083050";
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="program-detail-overlay"
    >
      <motion.article
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        data-testid={`program-detail-${p.id}`}
      >
        <div className="h-2 w-full" style={{ backgroundColor: color }} />
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span
                className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${color}1a`, color }}
              >
                {p.category}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl text-tc-ink leading-tight mt-2">{p.name}</h2>
            </div>
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <button
                  onClick={onEdit}
                  data-testid="program-detail-edit"
                  className="h-9 px-3 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-slate-100"
                  style={{ color }}
                >
                  <Pencil size={14} /> Edit
                </button>
              )}
              <button
                onClick={onClose}
                data-testid="program-detail-close"
                className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-3 gap-3 text-xs">
            {p.audience && (
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-1">Audience</div>
                <div className="text-slate-700">{p.audience}</div>
              </div>
            )}
            {p.format && (
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-1">Format</div>
                <div className="text-slate-700">{p.format}</div>
              </div>
            )}
            {p.duration && (
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-1">Duration</div>
                <div className="text-slate-700">{p.duration}</div>
              </div>
            )}
          </div>

          {p.description && (
            <section className="mt-6">
              <div className="font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-2">About</div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{p.description}</p>
            </section>
          )}

          {p.key_outcomes?.length > 0 && (
            <section className="mt-6">
              <div className="font-bold uppercase tracking-widest text-[10px] mb-2" style={{ color }}>Key outcomes</div>
              <ul className="space-y-1.5">
                {p.key_outcomes.map((o, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {p.notes && (
            <section className="mt-6 pt-5 border-t border-slate-100">
              <div className="font-bold uppercase tracking-widest text-[10px] text-slate-400 mb-2">Notes</div>
              <p className="text-sm text-slate-600 leading-relaxed">{p.notes}</p>
            </section>
          )}
        </div>
      </motion.article>
    </div>
  );
}


function ProgramEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [draft, setDraft] = useState(() => ({
    name: initial?.name || "",
    category: initial?.category || "Program",
    also_in: initial?.also_in || [],
    audience: initial?.audience || "",
    format: initial?.format || "",
    duration: initial?.duration || "",
    description: initial?.description || "",
    key_outcomes: (initial?.key_outcomes || []).join("\n"),
    notes: initial?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleAlsoIn = (cat) => {
    setDraft((d) => {
      const cur = d.also_in || [];
      return { ...d, also_in: cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat] };
    });
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name is required.");
    if (!draft.category) return toast.error("Pick a category.");
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        category: draft.category,
        also_in: draft.also_in.filter((c) => c && c !== draft.category),
        audience: draft.audience.trim() || null,
        format: draft.format.trim() || null,
        duration: draft.duration.trim() || null,
        description: draft.description.trim() || null,
        key_outcomes: draft.key_outcomes.split("\n").map((s) => s.trim()).filter(Boolean),
        notes: draft.notes.trim() || null,
      };
      if (isNew) {
        await api.post("/programs", payload);
        toast.success("Program added.");
      } else {
        await api.put(`/programs/${initial.id}`, payload);
        toast.success("Program updated.");
      }
      await onSaved();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    if (!window.confirm(`Delete "${initial.name}"? This can't be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/programs/${initial.id}`);
      toast.success("Program deleted.");
      await onSaved();
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  const color = CATEGORY_COLOR[draft.category] || "#083050";
  const INPUT = "w-full h-11 px-3 rounded-xl bg-white border-2 border-slate-200 focus:border-[#0091be] outline-none text-sm";

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="program-editor-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        data-testid="program-editor"
      >
        <div className="h-2 w-full" style={{ backgroundColor: color }} />
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-tc-ink">{isNew ? "Add program" : "Edit program"}</h2>
            <button onClick={onClose} data-testid="program-editor-close" className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
              <X size={18} />
            </button>
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <Label>Name</Label>
              <input
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="program-editor-name"
                className={INPUT}
                placeholder="e.g. Communication Training"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary category</Label>
                <select
                  value={draft.category}
                  onChange={(e) => set("category", e.target.value)}
                  data-testid="program-editor-category"
                  className={INPUT}
                >
                  {ALL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Also show under</Label>
                <div className="flex items-center gap-2 flex-wrap" data-testid="program-editor-also-in">
                  {ALL_CATEGORIES.filter((c) => c !== draft.category).map((c) => {
                    const active = (draft.also_in || []).includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleAlsoIn(c)}
                        className={`h-9 px-3 rounded-lg text-xs font-bold border-2 transition-all ${active ? "text-white" : "bg-white text-slate-600 hover:text-tc-ink"}`}
                        style={{
                          backgroundColor: active ? CATEGORY_COLOR[c] : undefined,
                          borderColor: active ? CATEGORY_COLOR[c] : "#e2e8f0",
                        }}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Audience</Label>
                <input value={draft.audience} onChange={(e) => set("audience", e.target.value)} data-testid="program-editor-audience" className={INPUT} placeholder="Organizations, schools…" />
              </div>
              <div>
                <Label>Format</Label>
                <input value={draft.format} onChange={(e) => set("format", e.target.value)} data-testid="program-editor-format" className={INPUT} placeholder="In-Person / Virtual" />
              </div>
              <div>
                <Label>Duration</Label>
                <input value={draft.duration} onChange={(e) => set("duration", e.target.value)} data-testid="program-editor-duration" className={INPUT} placeholder="3-4 Hours" />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={draft.description}
                onChange={(e) => set("description", e.target.value)}
                data-testid="program-editor-description"
                rows={5}
                className="w-full px-3 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-[#0091be] outline-none text-sm font-sans"
                placeholder="What is this program about? Who's it for?"
              />
            </div>

            <div>
              <Label>Key outcomes (one per line)</Label>
              <textarea
                value={draft.key_outcomes}
                onChange={(e) => set("key_outcomes", e.target.value)}
                data-testid="program-editor-outcomes"
                rows={4}
                className="w-full px-3 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-[#0091be] outline-none text-sm font-sans"
                placeholder="Improve collaboration.&#10;Reduce miscommunication.&#10;…"
              />
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <textarea
                value={draft.notes}
                onChange={(e) => set("notes", e.target.value)}
                data-testid="program-editor-notes"
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-[#0091be] outline-none text-sm font-sans"
                placeholder="Prerequisites, related programs, internal context…"
              />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
            <div>
              {!isNew && (
                <button
                  onClick={remove}
                  disabled={saving}
                  data-testid="program-editor-delete"
                  className="h-11 px-4 rounded-xl text-red-600 font-bold text-sm flex items-center gap-2 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="program-editor-save"
                className="h-11 px-5 rounded-xl text-white font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60"
                style={{ background: color }}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {isNew ? "Add program" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Label({ children }) {
  return <div className="tc-eyebrow text-slate-500 mb-1.5">{children}</div>;
}
